import { orgAPI } from "@/api/api";
import { IntegrationConnection } from "@cohesive/api-client";
import type { Entity } from "@cohesive/api-entities";
import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
} from "@tanstack/react-query";

// Each page is the full toolbox response (not just response.data) so the
// generic projections in data_generic survive alongside the typed entities in
// data — typed for display, generic for placement.
type SearchPage = Awaited<ReturnType<typeof orgAPI.toolbox.execute<Entity[]>>>;

interface UseNotionMCPSearchProps {
  connection: IntegrationConnection;
  query: string;
  teamspaceId?: string;
  options?: Partial<
    UseInfiniteQueryOptions<SearchPage, Error, InfiniteData<SearchPage>>
  >;
}

export function useNotionMCPSearch({
  connection,
  query = "",
  teamspaceId,
  options = {},
}: UseNotionMCPSearchProps) {
  return useInfiniteQuery({
    queryKey: [
      "notion-mcp-search",
      connection.id,
      query || "empty",
      teamspaceId || "all",
    ],
    queryFn: async ({ pageParam }) => {
      const actionInput = {
        query: query || " ", // Use space for broad results (empty string fails validation)
        query_type: "internal" as const,
        ...(pageParam || {}),
        ...(teamspaceId ? { teamspace_id: teamspaceId } : {}),
      };

      const response = await orgAPI.toolbox.execute<Entity[]>({
        integration_connection_id: connection.id,
        action_name: "notion-mcp-search",
        action_input: actionInput,
        // Typed entities drive the rich row UI; their generic projections ride
        // along in data_generic and are what get placed on the canvas.
        entity_format: "both",
      });
      return response;
    },
    getNextPageParam: () => {
      // Notion MCP search doesn't support pagination currently
      return undefined;
    },
    initialPageParam: {},
    enabled: !!connection.integration_id,
    staleTime: 1000 * 30, // 30 seconds
    ...options,
  });
}
